/**
 * Простая система кеширования для больших объектов на фронтенде
 */

interface CacheItem<T> {
  value: T
  timestamp: number
  ttl: number // Time to live в миллисекундах
}

export class SimpleCache {
  private cache: Map<string, CacheItem<any>> = new Map()

  /**
   * Получить значение из кеша
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key)
    
    if (!item) {
      return null
    }

    // Проверяем, не истек ли срок действия
    const now = Date.now()
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key)
      return null
    }

    return item.value as T
  }

  /**
   * Сохранить значение в кеш
   */
  set<T>(key: string, value: T, ttlMs: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl: ttlMs,
    })
  }

  /**
   * Удалить значение из кеша
   */
  delete(key: string): void {
    this.cache.delete(key)
  }

  /**
   * Удалить все ключи, начинающиеся с префикса
   */
  deleteByPrefix(prefix: string): void {
    const keysToDelete: string[] = []
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key)
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key))
  }

  /**
   * Очистить весь кеш
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Очистить истекшие записи
   */
  cleanup(): void {
    const now = Date.now()
    const keysToDelete: string[] = []
    
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        keysToDelete.push(key)
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key))
  }
}

// Глобальный экземпляр кеша
export const cache = new SimpleCache()

// Периодическая очистка истекших записей (каждые 5 минут)
if (typeof window !== 'undefined') {
  setInterval(() => {
    cache.cleanup()
  }, 5 * 60 * 1000)
}

/**
 * Генерация ключа кеша
 */
export function getCacheKey(prefix: string, ...args: (string | number | boolean | null | undefined)[]): string {
  const parts = args
    .map(arg => arg === null || arg === undefined ? '' : String(arg))
    .filter(Boolean)
  
  return `${prefix}:${parts.join(':')}`
}

